import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, Menu, UserCircle2 } from "lucide-react";
import WhatsAppMonitor from "@/components/WhatsAppMonitor";

const NavItems = ({ items, onNavigate }) => (
  <nav className="flex flex-col gap-1.5" data-testid="sidebar-nav">
    {items.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        data-testid={`nav-${item.key}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
            isActive
              ? "bg-emerald-800 text-white shadow-md shadow-emerald-900/20"
              : "text-slate-600 hover:bg-emerald-50 hover:text-emerald-900"
          }`
        }
      >
        <item.icon className="h-5 w-5 shrink-0" />
        <span>{item.label}</span>
      </NavLink>
    ))}
  </nav>
);

export default function DashboardLayout({ items, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const schoolName = user?.school_name || "لوحة المشرف العام";
  const roleLabel = user?.role === "SUPER_ADMIN" ? "المشرف العام" : "مرشد طلابي";

  const SidebarContent = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-center border-b border-slate-100 px-4 py-5">
        <Logo className="h-12" />
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <NavItems items={items} onNavigate={onNavigate} />
      </div>
      <div className="border-t border-slate-100 p-4">
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full justify-start gap-3 text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          <LogOut className="h-5 w-5" />
          تسجيل خروج
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {user?.role === "COUNSELOR" && <WhatsAppMonitor />}
      {/* Desktop sidebar (right) */}
      <aside className="fixed inset-y-0 right-0 z-40 hidden w-64 border-l border-slate-200 bg-white lg:block">
        <SidebarContent />
      </aside>

      <div className="lg:mr-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md md:px-8">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <SidebarContent onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="flex flex-col">
              <span className="text-sm font-bold text-emerald-900 md:text-base" data-testid="topbar-school-name">
                {schoolName}
              </span>
              <span className="text-xs text-slate-400">{title}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-slate-50 py-1.5 pl-3 pr-1.5">
            <div className="flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-slate-800" data-testid="topbar-user-name">
                {user?.name}
              </span>
              <span className="text-[11px] text-slate-400">{roleLabel}</span>
            </div>
            <UserCircle2 className="h-8 w-8 text-emerald-700" />
          </div>
        </header>

        <main className="mx-auto max-w-7xl overflow-x-hidden p-4 md:p-8">
          <div className="animate-fade-up">{/* page content */}</div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

import { Outlet } from "react-router-dom";
